import IosSwitch from "components/shared/IosSwitch";
import { BodyHighlight, SmallBody } from "helpers/TypographyComponents";
import type { CompanySettingList } from "hooks/useCompanySettings";
import type { ReactNode } from "react";

import type { SettingsCategoryId } from "./settingsCategories";
import { useBooleanSetting } from "./useBooleanSetting";

export type BooleanSettingConfig = {
    id: CompanySettingList;
    title: string;
    description: string;
    info?: string;
    category: SettingsCategoryId;
};

/**
 * Row-level type: used by the row component itself. Nested sub-settings (rendered
 * inside a parent row's `renderExtra`) legitimately have no category because they
 * are never registered in `BOOLEAN_SETTINGS` or displayed in a tab.
 */
type BooleanSettingRowConfig = Omit<BooleanSettingConfig, "category"> & { category?: SettingsCategoryId };

export const filterTextContent = (text: string, filter: string) => {
    if (!filter) return text;

    const lowerText = text.toLowerCase();
    const lowerFilter = filter.toLowerCase();
    const index = lowerText.indexOf(lowerFilter);
    if (index === -1) return text;

    const before = text.slice(0, index);
    const highlighted = text.slice(index, index + filter.length);
    const after = text.slice(index + filter.length);

    return (
        <span style={{ fontWeight: "inherit" }}>
            {before}
            <span style={{ backgroundColor: "yellow", fontWeight: "inherit" }}>{highlighted}</span>
            {after}
        </span>
    );
};

export const BooleanSettingRow = ({
    setting,
    filterText,
    renderExtra,
    onDisable,
}: {
    setting: BooleanSettingRowConfig;
    filterText: string;
    renderExtra?: (checked: boolean) => ReactNode;
    onDisable?: () => void;
}) => {
    const { checked, onChange } = useBooleanSetting(setting.id, onDisable);

    return (
        <>
            <div className="company-settings">
                <div>
                    <BodyHighlight>{filterTextContent(setting.title, filterText)}</BodyHighlight>
                    <SmallBody color="grey">{filterTextContent(setting.description, filterText)}</SmallBody>
                    {setting.info && <SmallBody color="info">{setting.info}</SmallBody>}
                </div>
                <IosSwitch checked={checked} onChange={onChange} />
            </div>
            {renderExtra?.(checked as boolean)}
        </>
    );
};
